import { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";
import { Eye, Trash2, Plus, KeyRound, Upload, Clock } from "lucide-react";

const ENVS = ["development", "staging", "production"];

// Patch 44's masked-by-default, reveal-and-log secret manager — reused verbatim. Category 4 adds
// env-scoping, expiry reminders, usage tracking, naming validation and individually-logged bulk import.
export default function SecretsManager({ deploymentId, compact }) {
  const confirm = useConfirm();
  const [secrets, setSecrets] = useState([]);
  const [revealed, setRevealed] = useState({});
  const [ns, setNs] = useState({ name: "", value: "", env: "production", used_by: "", expires_at: "" });
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");

  const load = useCallback(() => {
    if (!deploymentId) return;
    api.get(`/hosting/${deploymentId}/secrets`).then((r) => setSecrets(r.data.secrets || [])).catch(() => {});
    setRevealed({});
  }, [deploymentId]);
  useEffect(load, [load]);

  const reveal = async (sid) => {
    try { const { data } = await api.post(`/hosting/${deploymentId}/secrets/${sid}/reveal`); setRevealed((r) => ({ ...r, [sid]: data.value })); toast.info("Reveal logged to the audit trail"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const add = async () => {
    if (!ns.name.trim()) return;
    try { await api.post(`/hosting/${deploymentId}/secrets`, { ...ns, expires_at: ns.expires_at || null }); setNs({ name: "", value: "", env: "production", used_by: "", expires_at: "" }); load(); toast.success("Secret added"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (s) => {
    const ok = await confirm({ title: `Delete ${s.name}?`, body: "This permanently removes the secret. The value is not recoverable and your app will lose access to it.", confirmLabel: "Delete" });
    if (!ok) return;
    try { await api.delete(`/hosting/${deploymentId}/secrets/${s.id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const doImport = async () => {
    try { const { data } = await api.post(`/hosting/${deploymentId}/secrets/import`, { content: importText, env: ns.env }); toast.success(`Imported ${data.imported.length}, skipped ${data.skipped.length}`); setImportText(""); setImportOpen(false); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (!deploymentId) {
    return <p className="k-muted" style={{ fontSize: 13 }}>Provision a hosted deployment first to store this project's secret keys.</p>;
  }

  return (
    <div data-testid="secrets-manager">
      {!compact && <h4 className="k-heading" style={{ fontSize: 14, display: "flex", alignItems: "center", gap: 6, marginTop: 0 }}><KeyRound size={14} /> Secret keys</h4>}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8, alignItems: "center" }}>
        <input className="k-control" data-testid="secret-name" placeholder="KEY_NAME" style={{ maxWidth: 160 }} value={ns.name} onChange={(e) => setNs({ ...ns, name: e.target.value })} />
        <input className="k-control" data-testid="secret-value" placeholder="value" style={{ flex: 1, minWidth: 140 }} value={ns.value} onChange={(e) => setNs({ ...ns, value: e.target.value })} />
        <select className="k-control" data-testid="secret-env" style={{ width: "auto" }} value={ns.env} onChange={(e) => setNs({ ...ns, env: e.target.value })}>
          {ENVS.map((e) => <option key={e} value={e}>{e}</option>)}
        </select>
        <input className="k-control" data-testid="secret-usedby" placeholder="used by (e.g. checkout)" style={{ maxWidth: 150 }} value={ns.used_by} onChange={(e) => setNs({ ...ns, used_by: e.target.value })} />
        <input className="k-control" data-testid="secret-expiry" type="date" style={{ width: "auto" }} value={ns.expires_at} onChange={(e) => setNs({ ...ns, expires_at: e.target.value })} />
        <button className="k-btn k-btn-primary" data-testid="secret-add" onClick={add}><Plus size={14} /> Add</button>
        <button className="k-btn k-btn-ghost" data-testid="secret-import-toggle" onClick={() => setImportOpen((o) => !o)}><Upload size={14} /> Import .env</button>
      </div>
      {importOpen && (
        <div style={{ marginBottom: 10 }}>
          <textarea className="k-control" data-testid="secret-import-text" rows={4} placeholder={"# paste .env lines\nSTRIPE_API_KEY=sk_test_...\nMAIL_FROM=hello@acme.com"} value={importText} onChange={(e) => setImportText(e.target.value)} />
          <button className="k-btn k-btn-primary" data-testid="secret-import-run" onClick={doImport} style={{ marginTop: 6 }}>Import into {ns.env}</button>
          <span className="k-muted" style={{ fontSize: 12, marginLeft: 8 }}>Each line is masked and individually audit-logged.</span>
        </div>
      )}
      {secrets.length === 0 ? <p className="k-muted" style={{ fontSize: 13 }}>No secret keys yet.</p> :
        secrets.map((s) => (
          <div key={s.id} data-testid={`secret-row-${s.id}`} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, padding: "5px 0", borderTop: "1px solid var(--control)" }}>
            <strong style={{ minWidth: 130 }}>{s.name}</strong>
            <span className="k-badge" style={{ fontSize: 10 }}>{s.env}</span>
            <span className="mono" style={{ flex: 1, wordBreak: "break-all" }}>{revealed[s.id] || s.masked_value}</span>
            {s.used_by && <span className="k-muted" style={{ fontSize: 11 }} title="used by">↳ {s.used_by}</span>}
            {s.expires_at && <span className={s.expired ? "k-tag-red" : "k-tag-warn"} style={{ fontSize: 11 }} data-testid={`secret-expiry-${s.id}`}><Clock size={10} /> {s.expired ? "expired" : s.expires_at}</span>}
            <button className="k-btn k-btn-ghost" data-testid={`secret-reveal-${s.id}`} onClick={() => reveal(s.id)} style={{ padding: "4px 8px" }}><Eye size={13} /></button>
            <button className="k-btn k-btn-ghost" data-testid={`secret-delete-${s.id}`} onClick={() => del(s)} style={{ padding: "4px 8px" }}><Trash2 size={13} /></button>
          </div>
        ))}
      <p className="k-muted" style={{ fontSize: 12, marginTop: 10 }}>Values are hidden by default. Every reveal, add, edit, import and delete is logged to the Menu 10 audit trail. Names use UPPER_SNAKE_CASE so usage tracking stays reliable. An overwritten value is not recoverable.</p>
    </div>
  );
}
